import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { SwapHoriz as MoveIcon } from '@mui/icons-material';
import {
    fetchOwnedBusinesses,
    moveExternalBusinessMembership,
} from '../services/userManagementService';
import BusinessSelector from './BusinessSelector';
import BusinessFormFields from './BusinessFormFields';

const EMPTY_BUSINESS_FORM = {
    businessName: '',
    streetAddress: '',
    city: '',
    province: '',
    postalCode: '',
    country: '',
    phoneNumber: '',
    email: '',
};

const cleanPhoneNumber = (phoneNumber) => (phoneNumber || '').replace(/[^0-9]/g, '');

const handleDigitsOnlyChange = (field, value, setter) => {
    if (!/^[0-9]*$/.test(value)) return;
    setter(field, value);
};

const validateNewBusinessForm = (values) => {
    const errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!values.businessName.trim()) errors.businessName = 'Company name is required';
    if (values.email.trim() && !emailRegex.test(values.email))
        errors.email = 'Invalid email format';
    if (!values.email.trim()) errors.email = 'Company email is required';
    if (!values.phoneNumber.trim()) errors.phoneNumber = 'Company phone is required';
    if (!values.streetAddress.trim()) errors.streetAddress = 'Street address is required';
    if (!values.city.trim()) errors.city = 'City is required';
    if (!values.province.trim()) errors.province = 'Province is required';
    if (!values.postalCode.trim()) errors.postalCode = 'Postal code is required';
    if (!values.country.trim()) errors.country = 'Country is required';

    return errors;
};

const MoveCompanyDialog = ({ open, onClose, user, onMoveComplete }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [availableBusinesses, setAvailableBusinesses] = useState([]);
    const [loadingBusinesses, setLoadingBusinesses] = useState(false);
    const [moveSelection, setMoveSelection] = useState({
        businessId: null,
        isCreatingNew: false,
    });
    const [newBusinessFormData, setNewBusinessFormData] = useState(EMPTY_BUSINESS_FORM);
    const [moveErrors, setMoveErrors] = useState({});
    const [moveConfirmOpen, setMoveConfirmOpen] = useState(false);
    const [moveAffectedProjects, setMoveAffectedProjects] = useState([]);
    const [moveRequiresProjectRemoval, setMoveRequiresProjectRemoval] = useState(false);
    const [moveLoading, setMoveLoading] = useState(false);
    const [error, setError] = useState('');

    const resetState = () => {
        setMoveSelection({ businessId: null, isCreatingNew: false });
        setNewBusinessFormData({ ...EMPTY_BUSINESS_FORM });
        setMoveErrors({});
        setMoveConfirmOpen(false);
        setMoveAffectedProjects([]);
        setMoveRequiresProjectRemoval(false);
        setMoveLoading(false);
        setError('');
    };

    useEffect(() => {
        if (!open) {
            resetState();
            return undefined;
        }

        resetState();

        let cancelled = false;
        setLoadingBusinesses(true);
        fetchOwnedBusinesses()
            .then((data) => {
                if (!cancelled) setAvailableBusinesses(data || []);
            })
            .catch(() => {
                if (!cancelled) {
                    setMoveErrors({ selector: 'Unable to load companies. Please try again.' });
                }
            })
            .finally(() => {
                if (!cancelled) setLoadingBusinesses(false);
            });

        return () => {
            cancelled = true;
        };
    }, [open]);

    const selectableBusinesses = useMemo(
        () =>
            availableBusinesses.filter(
                (business) =>
                    business.subscriptionType === null && business.businessId !== user?.businessId
            ),
        [availableBusinesses, user?.businessId]
    );

    const selectedMoveBusiness = useMemo(
        () =>
            selectableBusinesses.find(
                (business) => business.businessId === moveSelection.businessId
            ),
        [moveSelection.businessId, selectableBusinesses]
    );

    if (!user) return null;

    const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
    const targetBusinessName = moveSelection.isCreatingNew
        ? newBusinessFormData.businessName.trim() || 'the new company'
        : selectedMoveBusiness?.businessName || 'the selected company';

    const handleMoveReview = () => {
        const errors = {};
        setMoveAffectedProjects([]);
        setMoveRequiresProjectRemoval(false);
        if (moveSelection.isCreatingNew) {
            Object.assign(errors, validateNewBusinessForm(newBusinessFormData));
        } else if (!moveSelection.businessId) {
            errors.selector = 'Choose a company or create a new one.';
        }

        setMoveErrors(errors);
        if (Object.keys(errors).length === 0) setMoveConfirmOpen(true);
    };

    const handleConfirmMove = async () => {
        setMoveLoading(true);
        setError('');

        const payload = {
            sourceMembershipId: user.businessMembershipId,
            ...(moveRequiresProjectRemoval ? { confirmProjectRemoval: true } : {}),
            ...(moveSelection.isCreatingNew
                ? {
                      newBusiness: {
                          businessName: newBusinessFormData.businessName.trim(),
                          email: newBusinessFormData.email.trim(),
                          phoneNumber: cleanPhoneNumber(newBusinessFormData.phoneNumber),
                          streetAddress: newBusinessFormData.streetAddress.trim(),
                          city: newBusinessFormData.city.trim(),
                          province: newBusinessFormData.province.trim(),
                          postalCode: newBusinessFormData.postalCode.trim(),
                          country: newBusinessFormData.country.trim(),
                          subscriptionType: null,
                      },
                  }
                : { targetBusinessId: moveSelection.businessId }),
        };

        try {
            const result = await moveExternalBusinessMembership(user.userId, payload);
            const removedProjectCount = moveRequiresProjectRemoval
                ? moveAffectedProjects.length
                : 0;
            const message =
                `${fullName || 'User'} moved to ${result.business.businessName}.` +
                (removedProjectCount
                    ? ` Removed from ${removedProjectCount} project${removedProjectCount === 1 ? '' : 's'}.`
                    : '');

            setMoveConfirmOpen(false);
            onMoveComplete({ result, message });
        } catch (err) {
            const impact = err.response?.data;
            if (err.response?.status === 409 && Array.isArray(impact?.affectedProjects)) {
                setMoveAffectedProjects(impact.affectedProjects);
                setMoveRequiresProjectRemoval(true);
                setMoveConfirmOpen(true);
            } else if (typeof impact === 'string') {
                setMoveConfirmOpen(false);
                setError(impact);
            } else {
                setMoveConfirmOpen(false);
                setError('Failed to move company membership. Please try again.');
            }
        } finally {
            setMoveLoading(false);
        }
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
                <DialogTitle>Move Company</DialogTitle>
                <DialogContent>
                    <Stack spacing={2.5} sx={{ mt: 1 }}>
                        {error && <Alert severity="error">{error}</Alert>}
                        <Alert severity="info">
                            If this user is on current projects, you&apos;ll be asked to confirm
                            removing that project access before the move is saved.
                        </Alert>
                        <BusinessSelector
                            businesses={selectableBusinesses}
                            value={moveSelection}
                            onChange={(selection) => {
                                setMoveSelection(selection);
                                setMoveErrors({});
                                setMoveAffectedProjects([]);
                                setMoveRequiresProjectRemoval(false);
                            }}
                            label="Search companies..."
                            createNewLabel="Create New Company"
                            helperText={
                                moveErrors.selector ||
                                (loadingBusinesses ? 'Loading companies...' : '')
                            }
                            error={!!moveErrors.selector}
                            required
                        />
                        {moveSelection.isCreatingNew && (
                            <BusinessFormFields
                                values={newBusinessFormData}
                                onChange={(field, value) => {
                                    setNewBusinessFormData((prev) => ({
                                        ...prev,
                                        [field]: value,
                                    }));
                                    setMoveErrors((prev) => ({ ...prev, [field]: undefined }));
                                    setMoveAffectedProjects([]);
                                    setMoveRequiresProjectRemoval(false);
                                }}
                                onPhoneChange={(field, value) =>
                                    handleDigitsOnlyChange(field, value, (key, nextValue) => {
                                        setNewBusinessFormData((prev) => ({
                                            ...prev,
                                            [key]: nextValue,
                                        }));
                                        setMoveErrors((prev) => ({ ...prev, [key]: undefined }));
                                        setMoveAffectedProjects([]);
                                        setMoveRequiresProjectRemoval(false);
                                    })
                                }
                                errors={moveErrors}
                            />
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={moveLoading} sx={{ textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleMoveReview}
                        disabled={moveLoading || loadingBusinesses}
                        sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            whiteSpace: 'normal',
                            lineHeight: 1.2,
                        }}
                    >
                        Review Move
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={moveConfirmOpen}
                onClose={() => setMoveConfirmOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Confirm Move</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Typography>
                            Move {fullName || 'this user'} from {user.businessName} to{' '}
                            {targetBusinessName}?
                        </Typography>
                        {moveRequiresProjectRemoval ? (
                            <Alert severity="warning">
                                This user will be removed from {moveAffectedProjects.length} current
                                project{moveAffectedProjects.length === 1 ? '' : 's'} under this GC.
                            </Alert>
                        ) : (
                            <Alert severity="info">
                                If this user is on current projects, you&apos;ll be asked to confirm
                                removing that project access before the move is saved.
                            </Alert>
                        )}
                        {moveAffectedProjects.length > 0 && (
                            <Box
                                component="ul"
                                sx={{
                                    m: 0,
                                    pl: 3,
                                    color: '#334155',
                                    maxHeight: 160,
                                    overflowY: 'auto',
                                }}
                            >
                                {moveAffectedProjects.map((project) => (
                                    <Typography
                                        component="li"
                                        variant="body2"
                                        key={project.projectId}
                                        sx={{ mb: 0.5 }}
                                    >
                                        {project.projectName || 'Unnamed project'}
                                    </Typography>
                                ))}
                            </Box>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setMoveConfirmOpen(false)}
                        disabled={moveLoading}
                        sx={{ textTransform: 'none' }}
                    >
                        Back
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirmMove}
                        disabled={moveLoading}
                        startIcon={moveLoading ? <CircularProgress size={16} /> : <MoveIcon />}
                        sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            whiteSpace: 'normal',
                            lineHeight: 1.2,
                        }}
                    >
                        {moveLoading
                            ? 'Moving...'
                            : moveRequiresProjectRemoval
                              ? 'Remove from Projects and Move'
                              : 'Move Company'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default MoveCompanyDialog;
